import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PiggyBank } from 'lucide-react';
import Button from '../../ui/Button';
import nurseryAccountsService from '../../../services/nurseryAccounts';
import { errorMessage } from '../../../utils/apiResponse';
import { money } from '../../../utils/nurseryMoney';
import { useOrgTimezone, formatDateTimeInTz, formatLocalDateInTz } from '../../../utils/timezone';
import type { CustomerDeposit, OrderDeposit } from '../../../types/nurseryAccounts';

/**
 * The order page's deposit: whether the order needs one, the deposit invoice
 * asked for and where it stands in Xero, and what the payer holds. Paid only
 * ever comes from Xero, once the deposit is reconciled there; nothing here
 * records a payment. The server works everything out.
 */
export function OrderDepositCard({ orderId }: { orderId: number }) {
  const { t } = useTranslation('nursery', { keyPrefix: 'OrderDepositCard' });
  const tz = useOrgTimezone();
  const [data, setData] = useState<OrderDeposit | null>(null);
  const [amount, setAmount] = useState('');
  // Ticked for a customer who has already paid: raised in Xero, not emailed.
  const [noEmail, setNoEmail] = useState(false);
  const [waiving, setWaiving] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await nurseryAccountsService.getOrderDeposit(orderId);
      if (res.success && res.data) {
        setData(res.data);
        setAmount(res.data.suggested ? res.data.suggested.toFixed(2) : '');
      }
    } catch {
      // Without it the order page still works; the card stays away.
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  if (!data.deposit_required && !data.deposits.length) return null;

  const run = async (fn: () => Promise<unknown>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setWaiving(false);
      setNote('');
      await load();
    } catch (e) {
      setError(errorMessage(e, fallback));
    }
    setBusy(false);
  };

  const call = async <T,>(p: Promise<{ success: boolean; data?: T }>, fallback: string) => {
    const res = await p;
    if (!res.success) throw new Error(errorMessage(res, fallback));
  };

  const request = () => {
    const value = Math.round(Number(amount.replace(/[$,\s]/g, '')) * 100) / 100;
    if (!(value > 0)) { setError(t('amountNeeded')); return; }
    const data = noEmail ? { amount: value, email_customer: false } : { amount: value };
    run(() => call(nurseryAccountsService.requestDeposit(orderId, data), t('requestFailed')), t('requestFailed'));
  };

  const where = (d: CustomerDeposit): string => {
    if (d.status === 'void') return t('stateVoid', { reason: d.void_reason || '' });
    if (d.status === 'paid') return d.drawn > 0 ? t('statePaidDrawn', { drawn: money(d.drawn), left: money(d.available) }) : t('statePaid');
    if (d.xero_sync_status === 'failed') return t('stateFailed', { error: d.xero_sync_error || '' });
    if (!d.xero_invoice_id) return t('stateSending');
    if (d.amount_paid > 0) return t('statePart', { paid: money(d.amount_paid), amount: money(d.amount) });
    return t('stateWaiting');
  };

  const notNeeded = (): string | null => {
    switch (data.reason) {
      case 'ready': return t('reasonReady');
      case 'under_minimum': return t('reasonUnderMinimum', { basis: money(data.basis ?? 0), min: money(data.min_order ?? 0) });
      case 'too_late': return t('reasonTooLate');
      case 'draft': return t('reasonDraft');
      case 'paid': return t('reasonPaid');
      case 'no_price': return t('reasonNoPrice');
      default: return null;
    }
  };

  const payer = data.payer?.name || t('thisCustomer');
  const why = !data.needed && !data.waived && !data.deposits.length ? notNeeded() : null;

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3 min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-base font-semibold text-neutral-900">
          <PiggyBank className="h-5 w-5 text-primary-600 shrink-0" /> {t('title')}
        </h2>
        <Link to={data.payer ? `/accounts/deposits?payer=${data.payer.id}` : '/accounts/deposits'} className="text-sm text-primary-700 hover:underline">
          {t('allDeposits')}
        </Link>
      </div>

      {error && <p className="text-sm text-red-700 break-words">{error}</p>}

      {data.held && (data.held.available > 0 || data.held.outstanding > 0) && (
        <p className="text-sm text-neutral-700 break-words">
          {t('held', { payer, available: money(data.held.available), outstanding: money(data.held.outstanding) })}
        </p>
      )}

      {data.deposits.map((d) => (
        <div key={d.id} className="rounded-lg border border-neutral-200 px-3 py-2 text-sm min-w-0">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3">
            <span className="font-medium text-neutral-900 break-all">{d.deposit_number || t('deposit')}</span>
            <span className="text-neutral-900">{money(d.amount)}</span>
          </div>
          <p className="text-neutral-600 break-words">
            {d.due_date ? t('due', { date: formatLocalDateInTz(String(d.due_date).slice(0, 10), tz) }) + ' · ' : ''}{where(d)}
          </p>
          {d.xero_sync_status === 'failed' && (
            <Button size="sm" variant="secondary" className="mt-2" disabled={busy}
              onClick={() => run(() => call(nurseryAccountsService.retryDepositXero(d.id), t('retryFailed')), t('retryFailed'))}>
              {t('retry')}
            </Button>
          )}
        </div>
      ))}

      {data.waived && (
        <div className="text-sm text-neutral-700 break-words space-y-2">
          <p>{t('waived', { by: data.waived.by_name || t('someone'), at: formatDateTimeInTz(data.waived.at, tz), note: data.waived.note })}</p>
          <Button size="sm" variant="secondary" disabled={busy}
            onClick={() => run(() => call(nurseryAccountsService.unwaiveDeposit(orderId), t('unwaiveFailed')), t('unwaiveFailed'))}>
            {t('unwaive')}
          </Button>
        </div>
      )}

      {why && <p className="text-sm text-neutral-600 break-words">{why}</p>}

      {data.needed && (
        <div className="space-y-3">
          <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 break-words">
            {t('needed', { pct: data.pct, basis: money(data.basis ?? 0), suggested: money(data.suggested ?? 0) })}
            {data.unpriced ? ` ${t('unpriced', { count: data.unpriced })}` : ''}
          </p>
          {!data.xero_active && <p className="text-sm text-neutral-700 break-words">{t('connectXero')}</p>}
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <label htmlFor="deposit-amount" className="block text-sm font-medium text-neutral-800">{t('amountLabel')}</label>
              <input id="deposit-amount" type="text" inputMode="decimal" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-36 rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500" />
            </div>
            <Button onClick={request} disabled={busy || !data.xero_active}>{busy ? t('working') : t('request')}</Button>
            {!waiving && (
              <Button variant="secondary" onClick={() => setWaiving(true)} disabled={busy}>{t('waive')}</Button>
            )}
          </div>
          <label className="flex items-start gap-2 text-sm text-neutral-800">
            <input type="checkbox" className="mt-1" checked={noEmail} onChange={(e) => setNoEmail(e.target.checked)} />
            <span className="break-words">{t('noEmail')}</span>
          </label>
          <p className="text-xs text-neutral-500 break-words">{t('requestHint', { days: data.due_days })}</p>
          {waiving && (
            <div className="space-y-2">
              <label htmlFor="deposit-waive-note" className="block text-sm font-medium text-neutral-800">{t('waiveNoteLabel')}</label>
              <textarea id="deposit-waive-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                placeholder={t('waiveNotePlaceholder')}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500" />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" disabled={busy || note.trim().length < 4}
                  onClick={() => run(() => call(nurseryAccountsService.waiveDeposit(orderId, note.trim()), t('waiveFailed')), t('waiveFailed'))}>
                  {t('waiveConfirm')}
                </Button>
                <Button size="sm" variant="secondary" onClick={() => { setWaiving(false); setNote(''); }}>{t('cancel')}</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
